import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Send } from 'lucide-react';
import { photo } from '../assets/photos';
import Seo from '../components/Seo';

const Reveal = ({
  children, delay = 0, className = '', y = 24,
}: {
  children: React.ReactNode; delay?: number; className?: string; y?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '',
    weddingDate: '', venue: '', message: '',
    services: { photography: false, videography: false, combo: false },
    referral: {
      instagram: false, facebook: false,
      otherSupplier: false, friendsFamily: false, googleAds: false,
    },
  });

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleServiceCheck  = (f: keyof typeof formData.services) =>
    setFormData(prev => ({ ...prev, services: { ...prev.services, [f]: !prev.services[f] } }));

  const handleReferralCheck = (f: keyof typeof formData.referral) =>
    setFormData(prev => ({ ...prev, referral: { ...prev.referral, [f]: !prev.referral[f] } }));

  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const accessKey = import.meta.env.VITE_WEB3FORMS_KEY;
    if (!accessKey) { setStatus('error'); return; }

    const picked = (o: Record<string, boolean>) =>
      Object.entries(o).filter(([, v]) => v).map(([k]) => k).join(', ') || '—';
    const honeypot = (e.currentTarget.elements.namedItem('botcheck') as HTMLInputElement | null)?.checked;

    setStatus('sending');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `New wedding inquiry from ${formData.firstName} ${formData.lastName}`,
          from_name: 'George & Co. Website',
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          wedding_date: formData.weddingDate || '—',
          venue: formData.venue || '—',
          interested_in: picked(formData.services),
          found_us_via: picked(formData.referral),
          message: formData.message || '—',
          botcheck: honeypot ?? false,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const faqs = [
    {
      q: 'How far in advance should we book?',
      a: 'We recommend booking at least 6 months in advance. Popular dates fill quickly, so the sooner the better — though as long as your date is still available, we\'d love to hear from you.',
    },
    {
      q: 'Can we see a preview quickly after the wedding?',
      a: 'Yes. You\'ll receive a Preview Photo Gallery within 24 hours and a Preview Video Reel within 48 hours of your wedding day.',
    },
    {
      q: 'How long until we receive everything?',
      a: 'Your full edited gallery and video will be delivered within 4 months of your wedding day, via your online gallery and USB.',
    },
    {
      q: 'What is your photography and videography style?',
      a: 'We combine candid documentary storytelling with carefully directed moments — capturing your wedding authentically while ensuring every important shot is covered.',
    },
    {
      q: 'What is your cancellation policy?',
      a: 'If needed, we can move your booking to a new date within the same year at no extra cost. If we\'re unable to accommodate the new date, our standard cancellation policy applies.',
    },
    {
      q: 'Do you provide raw files?',
      a: 'We don\'t provide raw files. We spend considerable time curating and editing your images and footage — the delivered product reflects the full story of your day.',
    },
    {
      q: 'How do we know you\'ll capture every important moment?',
      a: 'During your Final Meeting, we compile a tailored timeline and formal family photo list together to ensure nothing is missed on the day.',
    },
    {
      q: 'Can we see full galleries before booking?',
      a: 'Yes. Full galleries are available on our website and social media so you can get a complete sense of what to expect.',
    },
  ];

  return (
    <div className="min-h-screen bg-ivory overflow-x-hidden">
      <Seo route="/contact" />

      {/* ── Full-bleed intro image ───────────────────────────────── */}
      <div className="relative h-[55vh] min-h-[380px] overflow-hidden film-grain">
        <img
          src={photo('amber-erik/5.jpg')}
          fetchPriority="high"
          decoding="async"
          alt="Groom getting ready on the wedding morning — George & Co. photography"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.65)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/20 via-transparent to-ink/60" />
        <div className="absolute inset-0 flex items-end px-8 md:px-14 lg:px-20 pb-14">
          <div className="max-w-screen-xl w-full mx-auto">
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="label-sm text-white/65 mb-6"
            >
              Begin Your Story
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="heading-display text-white text-[clamp(2.5rem,6vw,6rem)]"
            >
              Let's talk about
              <br />
              <span className="heading-italic">your day.</span>
            </motion.h1>
          </div>
        </div>
      </div>

      {/* ── Contact form ─────────────────────────────────────────── */}
      <section className="bg-ivory py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">

            {/* Left — contact info */}
            <div className="lg:col-span-4">
              <Reveal>
                <p className="label-sm text-mist mb-6">Get in touch</p>
                <h2 className="heading-display text-ink text-[clamp(2rem,3.5vw,3.5rem)] mb-8 leading-[1.05]">
                  A limited number<br />
                  <span className="heading-italic">of weddings each year.</span>
                </h2>
                <p className="body-light mb-12">
                  We give every couple the focus and care their day deserves. Reach out and let's see if we're the right fit for your story.
                </p>
              </Reveal>

              <Reveal delay={0.15} className="space-y-6 border-t border-silver/40 pt-8">
                <div>
                  <p className="label-sm text-mist mb-1">Email</p>
                  <a href="mailto:info@georgeandco.co.za" className="link-underline text-ink">
                    info@georgeandco.co.za
                  </a>
                </div>
                <div>
                  <p className="label-sm text-mist mb-1">Location</p>
                  <p className="label-sm text-ink/60">Pretoria, South Africa</p>
                </div>
                <div>
                  <p className="label-sm text-mist mb-1">Coverage</p>
                  <p className="label-sm text-ink/60">South Africa & Worldwide</p>
                </div>
              </Reveal>
            </div>

            {/* Right — form */}
            <Reveal delay={0.15} className="lg:col-span-8">
              {status === 'sent' ? (
                <div className="py-16 border-t border-silver/40">
                  <p className="label-sm text-mist mb-4">Thank you</p>
                  <h3 className="heading-display text-ink text-[clamp(1.8rem,3vw,2.8rem)] mb-4">
                    Your inquiry is <span className="heading-italic">on its way.</span>
                  </h3>
                  <p className="body-light">We'll be in touch soon.</p>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-9">
                <input type="checkbox" name="botcheck" className="hidden" tabIndex={-1} autoComplete="off" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                  <div>
                    <label className="label-sm text-mist block mb-2">First Name *</label>
                    <input name="firstName" type="text" required
                      value={formData.firstName} onChange={handleInput}
                      placeholder="First name" className="input-cinematic" />
                  </div>
                  <div>
                    <label className="label-sm text-mist block mb-2">Last Name *</label>
                    <input name="lastName" type="text" required
                      value={formData.lastName} onChange={handleInput}
                      placeholder="Last name" className="input-cinematic" />
                  </div>
                </div>

                <div>
                  <label className="label-sm text-mist block mb-2">Email *</label>
                  <input name="email" type="email" required
                    value={formData.email} onChange={handleInput}
                    placeholder="your@email.com" className="input-cinematic" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                  <div>
                    <label className="label-sm text-mist block mb-2">Wedding Date</label>
                    <input name="weddingDate" type="date"
                      value={formData.weddingDate} onChange={handleInput}
                      className="input-cinematic" />
                  </div>
                  <div>
                    <label className="label-sm text-mist block mb-2">Venue</label>
                    <input name="venue" type="text"
                      value={formData.venue} onChange={handleInput}
                      placeholder="Your wedding venue" className="input-cinematic" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-9">
                  <div>
                    <label className="label-sm text-mist block mb-5">Interested in *</label>
                    <div className="space-y-3.5">
                      {([
                        ['photography', 'Photography'],
                        ['videography', 'Videography'],
                        ['combo',       'Photo & Video Combo'],
                      ] as [keyof typeof formData.services, string][]).map(([k, lbl]) => (
                        <label key={k} className="flex items-center gap-3 cursor-pointer group">
                          <div
                            className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${formData.services[k] ? 'bg-ink border-ink' : 'border-silver group-hover:border-ink/40'}`}
                          >
                            {formData.services[k] && (
                              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5">
                                <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.4" fill="none" />
                              </svg>
                            )}
                          </div>
                          <input type="checkbox" checked={formData.services[k]} onChange={() => handleServiceCheck(k)} className="sr-only" />
                          <span className="label-sm text-mist group-hover:text-ink transition-colors duration-300">{lbl}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="label-sm text-mist block mb-5">How did you find us?</label>
                    <div className="space-y-3.5">
                      {([
                        ['instagram',     'Instagram'],
                        ['facebook',      'Facebook'],
                        ['friendsFamily', 'Friends & Family'],
                        ['otherSupplier', 'Another Supplier'],
                        ['googleAds',     'Google'],
                      ] as [keyof typeof formData.referral, string][]).map(([k, lbl]) => (
                        <label key={k} className="flex items-center gap-3 cursor-pointer group">
                          <div
                            className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${formData.referral[k] ? 'bg-ink border-ink' : 'border-silver group-hover:border-ink/40'}`}
                          >
                            {formData.referral[k] && (
                              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5">
                                <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.4" fill="none" />
                              </svg>
                            )}
                          </div>
                          <input type="checkbox" checked={formData.referral[k]} onChange={() => handleReferralCheck(k)} className="sr-only" />
                          <span className="label-sm text-mist group-hover:text-ink transition-colors duration-300">{lbl}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="label-sm text-mist block mb-2">Tell us about your day</label>
                  <textarea name="message" rows={5}
                    value={formData.message} onChange={handleInput}
                    placeholder="Share as much or as little as you'd like..."
                    className="input-cinematic resize-none" />
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="flex items-center gap-4 bg-ink text-ivory px-10 py-4 label-sm hover:opacity-70 transition-opacity duration-500"
                  >
                    {status === 'sending' ? 'Sending…' : 'Send Inquiry'}
                    <Send size={13} />
                  </button>
                  {status === 'error' && (
                    <p className="label-sm text-red-700">
                      Something went wrong — please email info@georgeandco.co.za
                    </p>
                  )}
                </div>
              </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <section className="bg-bone py-20 md:py-32">
        <div className="max-w-screen-xl mx-auto px-8 md:px-14 lg:px-20">
          <Reveal>
            <p className="label-sm text-mist mb-5">Common Questions</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="heading-display text-ink text-[clamp(2.2rem,4.5vw,4.5rem)] mb-16">
              Everything you need to know
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-0 divide-y md:divide-y-0">
            {faqs.map((faq, i) => (
              <Reveal key={i} delay={i * 0.05} y={16}>
                <div className={`py-8 ${i < 2 ? 'md:border-t-0' : ''} border-t border-silver/40`}>
                  <h3 className="heading-display text-ink text-lg md:text-xl mb-3">{faq.q}</h3>
                  <p className="body-light text-sm md:text-[0.9375rem]">{faq.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
